import { ApolloError } from '@apollo/client'
import { Button, LinkButton } from '@pelotoncycle/design-system'
import { usePartnerUpdate } from 'data/hooks'
import { Partner_partner as TPartner } from 'data/queries/types/Partner'
import addDays from 'date-fns/addDays'
import { BaseSyntheticEvent } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AlertVariants, Box, useAlerts } from 'ui/components'
import { formatDatepickerDate } from 'utils/date'
import { deserializePartner, serializePartner, TDeserializedPartner } from 'utils/partner'
import { DateSelect, EnableEmailCheckbox } from './Fields'
import { FormSection } from './FormSection'
import { StyledForm } from './StyledForm'
import { StyledFormActionsContainer } from './StyledFormActionsContainer'
import { StyledMultiFieldContainer } from './StyledMultiFieldContainer'
import { StyledRemoveButton } from './StyledRemoveButton'

type TEmailsFormProps = {
  partner: TPartner
  previousStep: string
  onSuccess?: () => void
}

type TDeserializedPartnerKey = keyof TDeserializedPartner

const PartnerEmailsForm = ({ partner, onSuccess, previousStep }: TEmailsFormProps) => {
  const { t } = useTranslation()
  const { addAlert } = useAlerts()

  const { updatePartner, loading: updatePartnerLoading } = usePartnerUpdate({
    partnerId: partner.id,
  })

  const { overrideWelcomeEmail, terminationDate, name } = partner

  const welcomeEmailDate = partner?.welcomeEmailDate as string | null

  const welcomeEmailDateValue = deserializePartner.welcomeEmailDateValue(welcomeEmailDate)

  const deserializedTerminationDate = deserializePartner.terminationDate(terminationDate)

  const { control, formState, getValues, handleSubmit, setValue, clearErrors, watch } =
    useForm<TDeserializedPartner>({
      defaultValues: {
        overrideWelcomeEmail,
        welcomeEmailDate,
        welcomeEmailDateValue,
        terminationDate: deserializedTerminationDate,
      },
    })

  const { dirtyFields } = formState

  const isActuallyDirty = Object.keys(dirtyFields).length
  const shouldDisableSubmit = updatePartnerLoading || !isActuallyDirty

  const shouldDirty = true
  const shouldTouch = true

  const resetWelcomeEmailFields = () => {
    const formValues = getValues()
    // We're clearing errors on these fields during a reset, so only want to call
    // these functions if the fields have values (i.e. need to be reset) -- should preserve valid
    // errors on required fields. Cant trust accuracy of dirytValues :(
    const valuesExistToReset = !!formValues.welcomeEmailDateValue

    if (!valuesExistToReset) return

    setValue('welcomeEmailDateValue', null, { shouldDirty, shouldTouch })

    clearErrors(['welcomeEmailDateValue'])
  }

  const resetTerminationDate = () => {
    setValue('terminationDate', null, { shouldDirty })
  }

  const welcomeEmailsEnabled = !watch('overrideWelcomeEmail')

  type IndexablePartner = {
    [key: string]: unknown
  }

  const handleUpdatePartner: SubmitHandler<TDeserializedPartner> = input => {
    const fieldsToUpdate = Object.keys(dirtyFields).filter(field =>
      Boolean(dirtyFields[field as TDeserializedPartnerKey]),
    )

    const initialValue = {} as IndexablePartner

    const normalizedFields = fieldsToUpdate.reduce((acc, field) => {
      switch (field) {
        case 'welcomeEmailDateValue':
          // EM-3876: welcome_email_date only carries a calendar date - the backend
          // normalizes it to midnight UTC on save regardless of what's sent, so we
          // only need to build midnight UTC for the Y/M/D the user picked.
          acc.welcomeEmailDate = serializePartner.buildWelcomeEmailDate(
            input.welcomeEmailDateValue,
          )
          break
        case 'terminationDate':
          acc.terminationDate = input.terminationDate
            ? formatDatepickerDate(input.terminationDate)
            : null
          break
        default:
          acc[field as TDeserializedPartnerKey] = input[field as TDeserializedPartnerKey]
          break
      }

      return acc
    }, initialValue)

    const inputData = { ...normalizedFields }

    updatePartner({ input: inputData })
      .then(({ data: responseData }) => {
        if (responseData?.partnerUpdate?.requestOk) {
          addAlert({
            variant: AlertVariants.success,
            message: t('partner.update_succeeded', { name }),
            autoClose: 8000,
          })
          if (onSuccess) onSuccess()
        }
      })
      .catch(e => {
        let error = ''
        if (typeof e === 'string') error = e
        if (e instanceof ApolloError && typeof e.message === 'string') error = e.message
        addAlert({
          variant: AlertVariants.error,
          message: t('partner.update_failed', { error }),
          autoClose: 8000,
        })
      })
  }

  const submitForm = (e: BaseSyntheticEvent) => {
    handleSubmit(handleUpdatePartner)(e).catch(err => {
      /* eslint-disable-next-line */
      console.log('err ==>', err)
    })
  }

  const minDate = addDays(new Date(), 1)

  return (
    <StyledForm>
      <form onSubmit={submitForm}>
        <FormSection title={t('partner.welcome_email')}>
          <EnableEmailCheckbox
            control={control}
            name="overrideWelcomeEmail"
            text={t('partner.turn_on_emails', { type: 'welcome' })}
          />
        </FormSection>

        <FormSection
          title={t('partner.configure_details')}
          subtext={t('partner.welcome_email_configure_prompt')}
          borderBottom
        >
          <StyledMultiFieldContainer>
            <DateSelect
              control={control}
              name="welcomeEmailDateValue"
              minDate={minDate}
              isRequired={welcomeEmailsEnabled}
            />
            <StyledRemoveButton onClick={resetWelcomeEmailFields}>
              {t('partner.reset')}
            </StyledRemoveButton>
          </StyledMultiFieldContainer>
        </FormSection>

        <FormSection
          title={t('partner.termination_date')}
          subtext={t('partner.termination_date_help')}
        >
          <DateSelect
            control={control}
            name="terminationDate"
            minDate={minDate}
            isRequired={false}
          />

          <StyledRemoveButton onClick={resetTerminationDate}>
            {t('partner.reset')}
          </StyledRemoveButton>
        </FormSection>

        <StyledFormActionsContainer>
          <Box width="126px" marginRight="16px">
            <LinkButton
              color="dark"
              variant="outline"
              component={Link}
              to={previousStep}
              text={t('partner.go_back')}
              width="adaptive"
            />
          </Box>
          <Box width="126px">
            <Button
              type="submit"
              data-testid="partnerEmailsUpdate"
              width="adaptive"
              isDisabled={shouldDisableSubmit}
            >
              {t('partner.save_and_continue')}
            </Button>
          </Box>
        </StyledFormActionsContainer>
      </form>
    </StyledForm>
  )
}

export { PartnerEmailsForm }
